import { collection } from 'infra/collection'
import { MessagingAPI } from 'messaging/API'
import { MessageEventsDTO } from 'messaging/dtos/messages'
import { VoiceCallDto } from 'messaging/dtos/voice-calls'
import { concatMap, share } from 'rxjs'
import memoize from 'memoizee'
import { Id, PrimeProps } from 'types/definitions'
import { filterNonNil } from 'utils/rx-util'
import { VoiceCall, VoiceCallStatus } from './VoiceCall'

export default class VoiceCallStore {
  /**
   * Call events.
   */
  readonly events$ = this.api.events$.pipe(
    // Ensures events are processed serially.
    concatMap(this.handleServerEvent.bind(this)),
    filterNonNil(),
    share()
  )

  private readonly calls = collection<VoiceCallDto, VoiceCall>({
    create: () => new VoiceCall(),
  })

  constructor(private api: MessagingAPI) {
    this.fetchCall = memoize(this.fetchCall.bind(this), {
      length: 1,
      promise: true,
    })
  }

  callsForConversation(conversationId: Id) {
    return this.calls.filter((c) => c.conversationId === conversationId)
  }

  callById(callId: string | null) {
    return this.calls.find((c) => c.id === callId) ?? null
  }

  async fetchCall(callId: Id) {
    const call = await this.api.voiceCalls.getCall(Id.assert(callId))

    return this.calls.set({
      ...call,
      id: callId,
    })
  }

  async fetchConversationCalls(conversationId: Id) {
    return await this.api.voiceCalls
      .getByConversationId(Id.assert(conversationId))
      .then((res) => res.map(this.calls.set))
  }

  /**
   * Prepares a call and sets its caller's twilio auth token
   */
  async prepareCall(conversationId: Id, recipientUserPublicId: string) {
    const { call_id, twilio_token } = await this.api.voiceCalls.prepareCall(
      Id.assert(conversationId),
      Id.assert(recipientUserPublicId)
    )

    const call = this.primeCall({ id: call_id })
    call.setCallerTwilioToken(twilio_token.token)

    const fetched = await this.fetchCall(call_id)

    return {
      callId: fetched.id,
      twilioToken: twilio_token.token,
    }
  }

  private primeCall(props: PrimeProps<VoiceCallDto, 'id'>) {
    return this.calls.set({ ...props })
  }

  /**
   * Handles realtime events from the server and maps
   * them to application events.
   *
   * @param event
   * @private
   */
  private async handleServerEvent(event: MessageEventsDTO) {
    switch (event.type) {
      case 'PARTICIPANT_ADDED': {
        const conversationId = event.payload.conversation_id
        await this.fetchConversationCalls(conversationId)
        return
      }
      case 'VOICE_CALL_PREPARED': {
        const call = await this.fetchCall(event.payload.id)
        call.setCallStatus(VoiceCallStatus.Prepared)
        return call
      }
      case 'VOICE_CALL_CONNECTING': {
        const call = await this.fetchCall(event.payload.id)
        call.setCallStatus(VoiceCallStatus.Connecting)
        return call
      }
      case 'VOICE_CALL_RINGING': {
        const call = await this.fetchCall(event.payload.id)
        call.setCallStatus(VoiceCallStatus.Ringing)
        return call
      }
      case 'VOICE_CALL_ANSWERED': {
        const call = await this.fetchCall(event.payload.id)
        call.setInProgress(new Date(event.payload.date_answered))
        return call
      }
      case 'VOICE_CALL_COMPLETED': {
        const call = await this.fetchCall(event.payload.id)
        call.setCallStatus(VoiceCallStatus.Completed)
        return call
      }
      case 'VOICE_CALL_RECORDING_READY':
        return await this.fetchCall(event.payload.id)
    }
  }
}
